import { FC, ReactNode, Children, isValidElement, ReactElement } from 'react';
import { useReducedMotion } from 'motion/react';
import { ease, duration } from 'styled/tools/motion';
import { ButtonWrapper, LinkWrapper, IconWrapper } from './styles';

export type ButtonSize = 'sm' | 'md';

export interface ButtonProps {
  light?: boolean;
  glass?: boolean;
  outline?: boolean;
  size?:
    | ButtonSize
    | { base: ButtonSize; tablet?: ButtonSize; desktop?: ButtonSize };
  href?: string;
  newWindow?: boolean;
  onClick?: (event?: any) => void;
  children: ReactNode;
  disabled?: boolean;
  animate?: boolean; // icon is hidden by default, animates in on hover
  className?: string;
  type?: 'button' | 'submit' | 'reset';
  'aria-label'?: string;
  'data-testid'?: string;
  'aria-describedby'?: string;
  'aria-expanded'?: boolean;
  'aria-pressed'?: boolean | 'mixed';
  'aria-controls'?: string;
  'aria-haspopup'?: boolean | 'menu' | 'listbox' | 'tree' | 'grid' | 'dialog';
  'aria-busy'?: boolean;
}

// Animation variants

const cubicEase = ease as [number, number, number, number];
const transition = { ease: cubicEase, duration };

const hidden = {
  width: 0,
  opacity: 0,
  marginLeft: 0,
  marginRight: 0,
  transition,
};
const visible = { width: 'auto', opacity: 1, transition };

const trailingIconVariants = {
  hidden,
  visible: { ...visible, marginLeft: 'var(--spacing-4)', transition },
};
const leadingIconVariants = {
  hidden,
  visible: { ...visible, marginRight: 'var(--spacing-4)', transition },
};

const animateProps = {
  initial: 'hidden' as const,
  whileHover: 'visible' as const,
  whileFocus: 'visible' as const,
  whileTap: 'visible' as const,
};

// Helpers

const isIcon = (child: ReactNode): child is ReactElement =>
  isValidElement(child) && typeof (child as ReactElement).type !== 'string';

const splitChildren = (children: ReactNode) => {
  const arr = Children.toArray(children);
  let leading: ReactElement | null = null;
  let trailing: ReactElement | null = null;
  const text: ReactNode[] = [];

  for (const [i, child] of arr.entries()) {
    if (isIcon(child)) {
      if (i === 0) leading = child;
      else trailing = child;
    } else {
      text.push(child);
    }
  }

  return { leading, trailing, text };
};

// Component

const Button: FC<ButtonProps> = ({
  light,
  glass,
  outline,
  size = { base: 'sm', desktop: 'md' },
  href,
  newWindow,
  disabled,
  onClick,
  children,
  animate,
  className,
  type = 'button',
  'aria-label': ariaLabel,
  'data-testid': dataTestId,
  'aria-describedby': ariaDescribedby,
  'aria-expanded': ariaExpanded,
  'aria-pressed': ariaPressed,
  'aria-controls': ariaControls,
  'aria-haspopup': ariaHaspopup,
  'aria-busy': ariaBusy,
}) => {
  const shouldReduceMotion = useReducedMotion();

  const base = typeof size === 'string' ? size : size.base;
  const tablet = typeof size === 'string' ? undefined : size.tablet;
  const desktop = typeof size === 'string' ? undefined : size.desktop;

  const styleProps = {
    $light: light,
    $glass: glass,
    $outline: outline,
    $sizeBase: base,
    $sizeTablet: tablet,
    $sizeDesktop: desktop,
    $animate: animate,
  };

  let content: ReactNode = children;

  if (animate) {
    const { leading, trailing, text } = splitChildren(children);
    if (shouldReduceMotion) {
      content = (
        <>
          {leading}
          {text}
          {trailing}
        </>
      );
    } else {
      content = (
        <>
          {leading && (
            <IconWrapper variants={leadingIconVariants}>{leading}</IconWrapper>
          )}
          {text}
          {trailing && (
            <IconWrapper variants={trailingIconVariants}>
              {trailing}
            </IconWrapper>
          )}
        </>
      );
    }
  }

  const ariaProps = {
    'aria-label': ariaLabel,
    'aria-describedby': ariaDescribedby,
    'aria-expanded': ariaExpanded,
    'aria-pressed': ariaPressed,
    'aria-controls': ariaControls,
    'aria-haspopup': ariaHaspopup,
    'aria-busy': ariaBusy,
  };

  const motionProps = animate && !shouldReduceMotion ? animateProps : {};

  if (href) {
    return (
      <LinkWrapper
        {...styleProps}
        {...motionProps}
        {...ariaProps}
        data-testid={dataTestId}
        href={href}
        target={newWindow ? '_blank' : undefined}
        rel={newWindow ? 'noopener noreferrer' : undefined}
        className={className}
        onClick={onClick}
      >
        {content}
        {newWindow && !ariaLabel && (
          <span className='screenreader-only'>(opens in new tab)</span>
        )}
      </LinkWrapper>
    );
  }

  return (
    <ButtonWrapper
      {...styleProps}
      {...motionProps}
      {...ariaProps}
      data-testid={dataTestId}
      type={type}
      disabled={disabled}
      onClick={onClick}
      className={className}
    >
      {content}
    </ButtonWrapper>
  );
};

export default Button;

/**
 * HOW TO IMPLEMENT - BUTTON
 * ---------------
 *
 * @example Primary (default)
 * <Button onClick={handleClick}>Get Started</Button>
 *
 * @example Light / Glass / Glass Light
 * <Button light>Learn More</Button>
 * <Button glass>Shop Now</Button>
 * <Button glass light>Shop Now</Button>
 *
 * @example Outline
 * <Button outline>Subscribe</Button>
 * <Button outline light>Subscribe</Button>
 *
 * @example With icon
 * <Button>Get Started <ArrowForward /></Button>
 *
 * @example Animated icon (reveals on hover)
 * <Button animate>Get Started <ArrowForward /></Button>
 * <Button animate><ArrowForward /> Go Back</Button>
 *
 * @example Link
 * <Button light href="https://seed.com" newWindow>Read More</Button>
 *
 * @example Disabled
 * <Button disabled>Unavailable</Button>
 *
 * @example Icon-only (requires aria-label)
 * <Button outline aria-label="Close"><CloseIcon /></Button>
 *
 * @example Toggle (aria-pressed)
 * <Button aria-pressed={isActive} onClick={toggle}>Favorite</Button>
 *
 * @example Submit form
 * <Button type="submit">Submit</Button>
 *
 * @example Async (swap the label for a loader while busy)
 * <Button aria-busy={isLoading} aria-label={isLoading ? 'Adding to cart' : undefined}>
 *   {isLoading ? <Loader /> : 'Add to cart'}
 * </Button>
 */
